import React, { useState, useRef, useEffect } from "react";
import { useTooltip } from "context/TooltipProvider";
import { Referral, Roles } from "types";
import { hasRole } from "utils/helper";
import { useAuth } from "context/AuthContext";

interface ActionsDropdownProps {
  onSuspensionClick?: React.Dispatch<React.SetStateAction<boolean>>;
  onReinitiateClick?: React.Dispatch<React.SetStateAction<boolean>>;
  onForwardClick?: React.Dispatch<React.SetStateAction<boolean>>;
  action_status?: string;
  disabled?: boolean;
  referral?: Referral;
  handleCreateTerminationRequestClick?: () => void;
  handleAddAssetRecoveryDecisionClick?: () => void;
  handleAddAssetDecisionClick?: () => void;
}

const ActionsDropdown: React.FC<ActionsDropdownProps> = ({
  onSuspensionClick,
  onReinitiateClick,
  disabled = false,
  onForwardClick,
  action_status,
  referral,
  handleCreateTerminationRequestClick,
  handleAddAssetRecoveryDecisionClick,
  handleAddAssetDecisionClick,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();

  const isAssetCoordinator = hasRole(loginData?.roles, [
    Roles.assetCoordinator,
  ]);

  const isAssetProsecutor = hasRole(loginData?.roles, [Roles.assetProsecutor]);
  const shouldShowReinitate = ![
    "Reinitiate Approved",
    "Reinitiate Rejected",
    "Reinitiate Requested",
  ].includes(action_status as string);
  const shouldShowSuspension = ![
    "Suspension Requested",
    "Suspend",
    "Suspension Rejected",
  ].includes(action_status as string);
  const hasTerminationData =
    referral?.termination_reason ||
    referral?.termination_date ||
    (referral?.termination_order_documents &&
      referral.termination_order_documents.length > 0);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSuspensionClick = () => {
    onSuspensionClick?.(true);
    setIsOpen(false);
  };

  const handleReinitiateClick = () => {
    onReinitiateClick?.(true);
    setIsOpen(false);
  };
  const handleForwardClick = () => {
    onForwardClick?.(true);
    setIsOpen(false);
  };

  // Calculate available options for Asset Prosecutor
  const hasAssetProsecutorOptions =
    isAssetProsecutor &&
    ((shouldShowSuspension && onSuspensionClick) ||
      (shouldShowReinitate && onReinitiateClick) ||
      (!hasTerminationData && handleCreateTerminationRequestClick) ||
      (referral?.action_status === "Investigation Ongoing" &&
        !referral?.is_prohabition_requested &&
        handleAddAssetDecisionClick) ||
      (referral?.action_status === "Prohibition Final Verdict" &&
        referral?.is_prohabition_requested &&
        handleAddAssetRecoveryDecisionClick));

  // Calculate available options for Asset Coordinator
  const hasAssetCoordinatorOptions = isAssetCoordinator && onForwardClick;

  // Check if there are any available options
  const hasAvailableOptions =
    hasAssetProsecutorOptions || hasAssetCoordinatorOptions;

  // Check if button should be disabled
  const isButtonDisabled = disabled || !hasAvailableOptions;

  const handleToggleDropdown = () => {
    if (isButtonDisabled) {
      showTooltip("Actions are currently disabled", "info");
      return;
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={handleToggleDropdown}
        disabled={isButtonDisabled}
        className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-gray-300 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
          isButtonDisabled
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-white text-gray-700 hover:text-gray-900"
        }`}
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
        Actions
        <svg
          className={`w-4 h-4 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && hasAvailableOptions && (
        <div className="absolute right-0 z-10 mt-2 w-60 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1">
            {/* Asset Prosecutor Options */}
            {isAssetProsecutor && (
              <>
                {/* Suspension Option */}
                {shouldShowSuspension && onSuspensionClick && (
                  <button
                    onClick={handleSuspensionClick}
                    className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                  >
                    <svg
                      className="w-4 h-4 text-yellow-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                    <span className="font-medium">Request Suspension</span>
                  </button>
                )}
                {/* Reinitiate Option */}
                {shouldShowReinitate && onReinitiateClick && (
                  <button
                    onClick={handleReinitiateClick}
                    className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                  >
                    <svg
                      className="w-4 h-4 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    <span className="font-medium">Reinitiate</span>
                  </button>
                )}
                {/* Termination Request Option */}
                {!hasTerminationData && handleCreateTerminationRequestClick && (
                  <button
                    className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                    onClick={handleCreateTerminationRequestClick}
                  >
                    <svg
                      className="w-4 h-4 text-red-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    <span className="font-medium">Termination Request</span>
                  </button>
                )}
                {/* Prohibition Request Option */}
                {referral?.action_status === "Investigation Ongoing" &&
                  // !referral?.is_prohabition_requested &&
                  handleAddAssetDecisionClick && (
                    <button
                      className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                      onClick={handleAddAssetDecisionClick}
                    >
                      <svg
                        className="w-4 h-4 text-purple-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="font-medium">
                        Add Prohibition Request
                      </span>
                    </button>
                  )}
                {/* Asset Recovery Option */}
                {referral?.prosecutor_decision_choice ===
                  "proceed_to_asset_recovery" &&
                  handleAddAssetRecoveryDecisionClick && (
                    <button
                      className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                      onClick={handleAddAssetRecoveryDecisionClick}
                    >
                      <svg
                        className="w-4 h-4 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                        />
                      </svg>
                      <span className="font-medium">Add Asset Recovery</span>
                    </button>
                  )}
              </>
            )}
            {/* Asset Coordinator Options */}
            {isAssetCoordinator && onForwardClick && (
              <button
                onClick={handleForwardClick}
                className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
              >
                <svg
                  className="w-4 h-4 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
                <span className="font-medium">Forward</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionsDropdown;
